import React, { Component } from 'react';
import bookConverters from '../utilities/bookConversions';
import osisConverters from '../utilities/osisConversions';
import books from '../data/books';
import BibleStudyDrawer from './BibleStudyDrawer';
import { ThemeContext } from './Core/ThemeContext';
import { SourcePermissions } from './Core/TextComponents';

function buildParagraphOutline(lines) {
    if (!lines.length) return '';

    const lastIndex = lines.length - 1;
    const sharedRight = lines.length === 1
        ? lines[0].right
        : Math.max(...lines.slice(0, lastIndex).map((line) => line.right));
    const joinedLines = lines.map((line, index) => (
        index === lastIndex ? line : { ...line, right: sharedRight }
    ));
    const firstLine = joinedLines[0];
    const lastLine = joinedLines[lastIndex];
    const points = [
        { x: firstLine.left, y: firstLine.top },
        { x: firstLine.right, y: firstLine.top },
    ];

    for (let index = 1; index < joinedLines.length; index += 1) {
        const previousLine = joinedLines[index - 1];
        const currentLine = joinedLines[index];
        const joinY = (previousLine.bottom + currentLine.top) / 2;
        points.push({ x: previousLine.right, y: joinY });
        points.push({ x: currentLine.right, y: joinY });
    }

    points.push({ x: lastLine.right, y: lastLine.bottom });
    points.push({ x: lastLine.left, y: lastLine.bottom });

    for (let index = joinedLines.length - 2; index >= 0; index -= 1) {
        const currentLine = joinedLines[index];
        const nextLine = joinedLines[index + 1];
        const joinY = (currentLine.bottom + nextLine.top) / 2;
        points.push({ x: nextLine.left, y: joinY });
        points.push({ x: currentLine.left, y: joinY });
    }

    const uniquePoints = points.filter((point, index) => (
        index === 0 || point.x !== points[index - 1].x || point.y !== points[index - 1].y
    ));
    const cornerPoints = uniquePoints.filter((point, index) => {
        const previousPoint = uniquePoints[(index - 1 + uniquePoints.length) % uniquePoints.length];
        const nextPoint = uniquePoints[(index + 1) % uniquePoints.length];
        return !((previousPoint.x === point.x && point.x === nextPoint.x)
            || (previousPoint.y === point.y && point.y === nextPoint.y));
    });

    const radius = 7;
    const roundedPoints = cornerPoints.map((point, index) => {
        const previousPoint = cornerPoints[(index - 1 + cornerPoints.length) % cornerPoints.length];
        const nextPoint = cornerPoints[(index + 1) % cornerPoints.length];
        const previousDistance = Math.hypot(point.x - previousPoint.x, point.y - previousPoint.y);
        const nextDistance = Math.hypot(point.x - nextPoint.x, point.y - nextPoint.y);
        const cornerRadius = Math.min(radius, previousDistance / 2, nextDistance / 2);
        const before = {
            x: point.x + ((previousPoint.x - point.x) * cornerRadius) / previousDistance,
            y: point.y + ((previousPoint.y - point.y) * cornerRadius) / previousDistance,
        };
        const after = {
            x: point.x + ((nextPoint.x - point.x) * cornerRadius) / nextDistance,
            y: point.y + ((nextPoint.y - point.y) * cornerRadius) / nextDistance,
        };
        return { point, before, after };
    });

    if (!roundedPoints.length) return '';

    let path = `M ${roundedPoints[0].before.x} ${roundedPoints[0].before.y}`;
    roundedPoints.forEach(({ point, before, after }, index) => {
        if (index > 0) path += ` L ${before.x} ${before.y}`;
        path += ` Q ${point.x} ${point.y} ${after.x} ${after.y}`;
    });

    return `${path} Z`;
}

function JoinedParagraphOutline({ colorIndex, children }) {
    const containerRef = React.useRef(null);
    const textRef = React.useRef(null);
    const [outline, setOutline] = React.useState({ path: '', width: 0, height: 0 });

    const updateOutline = React.useCallback(() => {
        const container = containerRef.current;
        const text = textRef.current;
        if (!container || !text) return;

        const range = document.createRange();
        if (typeof range.getClientRects !== 'function'
            || typeof container.getBoundingClientRect !== 'function') {
            return;
        }
        range.selectNodeContents(text);
        const containerRect = container.getBoundingClientRect();
        const fragments = Array.from(range.getClientRects()).filter((rect) => rect.width > 1 && rect.height > 1);
        const lineGroups = [];

        fragments.forEach((fragment) => {
            const currentLine = lineGroups[lineGroups.length - 1];
            if (currentLine && fragment.top <= currentLine.top + 10) {
                currentLine.left = Math.min(currentLine.left, fragment.left);
                currentLine.right = Math.max(currentLine.right, fragment.right);
                currentLine.top = Math.min(currentLine.top, fragment.top);
                currentLine.bottom = Math.max(currentLine.bottom, fragment.bottom);
                return;
            }
            lineGroups.push({
                left: fragment.left,
                right: fragment.right,
                top: fragment.top,
                bottom: fragment.bottom,
            });
        });

        const paddingX = 8;
        const paddingY = 3;
        const lines = lineGroups.map((line) => ({
            left: line.left - containerRect.left - paddingX,
            right: line.right - containerRect.left + paddingX,
            top: line.top - containerRect.top - paddingY,
            bottom: line.bottom - containerRect.top + paddingY,
        }));

        setOutline({
            path: buildParagraphOutline(lines),
            width: container.clientWidth,
            height: container.clientHeight,
        });
    }, []);

    React.useLayoutEffect(() => {
        updateOutline();
        const resizeObserver = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(updateOutline);
        if (resizeObserver && containerRef.current) resizeObserver.observe(containerRef.current);
        window.addEventListener('resize', updateOutline);
        return () => {
            if (resizeObserver) resizeObserver.disconnect();
            window.removeEventListener('resize', updateOutline);
        };
    }, [updateOutline]);

    return (
        <p
            className="bible-paragraph-text bible-paragraph-outline"
            ref={containerRef}
            style={{ '--paragraph-color': `rgb(var(--color-raw-${colorIndex}))` }}
        >
            <svg
                className="bible-paragraph-outline-svg"
                aria-hidden="true"
                viewBox={`0 0 ${outline.width} ${outline.height}`}
                preserveAspectRatio="none"
            >
                <path className="bible-paragraph-outline-path" d={outline.path} />
            </svg>
            <span className="bible-paragraph-text-content" ref={textRef}>{children}</span>
        </p>
    );
}



class BibleReader extends Component {
    static contextType = ThemeContext;

    constructor(props) {
        super(props);
        this.state = { selectedVerse: null, drawerOpen: false };
    }

    static getVerseDetails(value) {
        const osisRef = value?.data?.what?.osisRef || '';
        const parts = osisRef.split('.');
        return {
            book: parts[0],
            chapter: parseInt(parts[1], 10) || 0,
            verse: parseInt(parts[2], 10) || 0,
        };
    }

    static getRangeDetails(range = '') {
        const parts = range.split('-');
        const start = BibleReader.getVerseDetails({ data: { what: { osisRef: parts[0] } } });
        const end = BibleReader.getVerseDetails({ data: { what: { osisRef: parts[parts.length - 1] } } });
        return { start, end };
    }

    static getParagraphKey(value, index) {
        const details = BibleReader.getVerseDetails(value);
        const heading = value?.data?.sentence?.heading;
        if (heading) return `heading:${heading}`;

        const book = books.booksData.data[details.book];
        const candidates = [];
        (book?.sections || []).forEach((section) => {
            (section.subsections || []).forEach((subsection) => {
                const range = BibleReader.getRangeDetails(subsection.osisRange);
                const startsBefore = details.chapter > range.start.chapter
                    || (details.chapter === range.start.chapter && details.verse >= range.start.verse);
                const endsAfter = details.chapter < range.end.chapter
                    || (details.chapter === range.end.chapter && details.verse <= range.end.verse);
                if (startsBefore && endsAfter) {
                    candidates.push({
                        key: subsection.osisRange,
                        span: (range.end.chapter - range.start.chapter) * 1000 + range.end.verse - range.start.verse,
                    });
                }
            });
        });

        if (candidates.length) {
            candidates.sort((first, second) => first.span - second.span);
            return `range:${candidates[0].key}`;
        }
        return `fallback:${index}`;
    }

    getParagraphs() {
        const paragraphs = [];
        let current = null;
        this.props.text.forEach((value, index) => {
            const key = BibleReader.getParagraphKey(value, index);
            if (!current || current.key !== key) {
                current = { key, verses: [] };
                paragraphs.push(current);
            }
            current.verses.push(value);
        });
        return paragraphs;
    }

    selectVerse = (value, event) => {
        const selectedElement = event?.currentTarget;
        this.setState({ selectedVerse: value, drawerOpen: true }, () => {
            if (selectedElement && window.matchMedia('(max-width: 768px)').matches) {
                selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            }
        });
    };

    closeDrawer = () => {
        this.setState({ drawerOpen: false });
    };

    openDrawer = () => {
        this.setState({ drawerOpen: true });
    };

    static processAPIData(response, chapter, verseStart = -1, verseEnd=-1) {
        console.log('Process: ', response.verses.length, chapter, verseStart, verseEnd)
        const text = [];
        console.log(response);
        let data = response.verses;
        const sources = response.sourceinfo;
        const subgroup = response.book.Subgroup;

        let finalVerse = -1;
    
        // used to be only if verse_ref.length == 3;
        if ((verseStart > -1)&&(verseEnd == -1))
        {
            data = [response.verses[verseStart - 1]];
            if ((!data) || (data.length === 0) || !data[0])
                this.handleRefError(chapter);
        }
        if ((verseStart > -1)&&(verseEnd > -1))
        {
            data = []
            let currentVerse = verseStart - 1;
            const tmpVerseEnd = (verseEnd == 0)?9999:verseEnd;

            while((currentVerse < tmpVerseEnd) && (currentVerse < response.verses.length))
            {
                data.push(response.verses[currentVerse]);
                currentVerse++;
                finalVerse = currentVerse;
            }

            console.log('data', data, verseStart, currentVerse, verseEnd);

            if ((!data) || (data.length === 0) || !data[0])
                this.handleRefError(chapter);
        }
    
        let title = '';
        let book = '';
    

        if (data.length > 0)
        {
            for (let i = 0; i < data.length; i += 1) {
                const verseinfo = data[i];
    
                text.push({
                    row: (i + 1),
                    text: verseinfo.verse,
                    data: verseinfo,
                });
    
                if (title === '') { title = `${data[i].what.name} ${data[i].what.chapter}`; }
                if (book === '') { book = `${data[i].what.osisRef.split('.')[0]}`; }

                if ((verseStart > -1)||(finalVerse > -1))
                {
                    title = `${data[i].what.name} ${data[i].what.chapter}:${verseStart}`;
        
                    if (finalVerse > -1)
                        title = `${title}-${finalVerse}`
                }
            }
        }
        else
        {
            book = response.book.BookCode;
            title = response.book.Name;
        }
    
        return {
            loading: false,
            text,
            title,
            sources,
            book,
            colorIndex: bookConverters.convertSubGroupToIndex(subgroup),
        };
    }

    render() {
        const aggregatedSources = this.props.sources;
        const paragraphs = this.getParagraphs();
        const selected = this.state.selectedVerse;
        const drawerOpen = this.state.drawerOpen;
        const colorIndex = this.props.colorIndex || 1;
        return (
            <>
                <div className="bible-paragraph-list">
                    {paragraphs.map((paragraph) => {
                        const firstVerse = paragraph.verses[0];
                        const lastVerse = paragraph.verses[paragraph.verses.length - 1];
                        const firstTitle = osisConverters.osisRefToVerseIndex(firstVerse.data.what.osisRef);
                        const lastTitle = osisConverters.osisRefToVerseIndex(lastVerse.data.what.osisRef);
                        const rangeTitle = firstTitle === lastTitle ? firstTitle : `${firstTitle}-${lastTitle}`;
                        return (
                            <div className="bible-paragraph-row" key={paragraph.key}>
                                <div className="bible-paragraph-gutter">{rangeTitle}.</div>
                                <div className="bible-paragraph-content">
                                    <JoinedParagraphOutline colorIndex={colorIndex}>
                                            {paragraph.verses.map((value, index) => {
                                                const verseTitle = osisConverters.osisRefToVerseIndex(value.data.what.osisRef);
                                                const isSelected = selected === value;
                                                return (
                                                    <React.Fragment key={value.data.what.osisRef || index}>
                                                        <span
                                                            className={`bible-verse-button${isSelected ? ' is-selected' : ''}`}
                                                            onClick={(event) => this.selectVerse(value, event)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter' || event.key === ' ') {
                                                                    event.preventDefault();
                                                                    this.selectVerse(value);
                                                                }
                                                            }}
                                                            role="button"
                                                            tabIndex="0"
                                                            aria-label={`Open study information for verse ${verseTitle}`}
                                                        >
                                                            <sup>{verseTitle}</sup>
                                                            {' '}{value.text}
                                                        </span>{' '}
                                                    </React.Fragment>
                                                );
                                            })}
                                    </JoinedParagraphOutline>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <BibleStudyDrawer
                    contents={selected}
                    sources={aggregatedSources}
                    colorIndex={colorIndex}
                    open={drawerOpen}
                    onClose={this.closeDrawer}
                    onOpen={this.openDrawer}
                />
                <div className="m-2">
                    <SourcePermissions
                        sources={(aggregatedSources.verse) ? aggregatedSources.verse : []}
                        ref = { this.sourcePermissionsRef }
                    />
                </div>
            </>
        )
    }
}

export default BibleReader;
